import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { Trash2, Image as ImageIcon, Video, Sparkles, Pencil, Upload, Layers, CheckCircle2 } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { studioPath } from '../utils/segment';
import { VirtuosoGrid } from 'react-virtuoso';
import { motion, useReducedMotion, type Variants } from 'motion/react';
import { SearchIcon, EditIcon, VideoIcon, DownloadIcon, Tag as TagIcon } from './icons/Icons';
import { GalleryItem } from '../types/gallery';
import { getImageExtFromUrl } from '../utils/imageFormat';
import PageHeader from './shared/PageHeader';
import campaignService, { Campaign } from '../services/campaignService';
import aiService from '../services/aiService';
import { useAuthStore } from '../stores/useAuthStore';
import TagSelector from './shared/TagSelector';
import tagService from '../services/tagService';
import FullscreenImageViewer, { ViewerImage } from './shared/FullscreenImageViewer';
import TagManagementModal from './shared/TagManagementModal';
import GroupedImageCard from './shared/GroupedImageCard';
import { ChannelFilterDropdown, type ChannelValue } from './shared/ChannelFilterDropdown';
import { Button, Input, Select, type SelectOption } from '../components/ui';
import { APP_HOME } from '../utils/segment';

// Interface for grouped images with variations
interface GroupedImage {
  id: string;
  src: string;
  thumbnailUrl?: string;
  width?: number;
  height?: number;
  variationsCount: number;
  variations: Array<{
    id: number;
    url: string;
    thumbnailUrl?: string;
    width?: number;
    height?: number;
    createdAt: string;
  }>;
  tags?: Array<{ id: number; name: string; color: string }>;
  createdAt: string;
}

interface GalleryPageProps {
  items: GalleryItem[];
  onEditItem: (item: GalleryItem) => void;
  onStartVideoCreation: (item: GalleryItem) => void;
}

// Page-load entrance variants: base=300ms, slow=500ms — numeric equivalents of
// --snapfy-duration-base and --snapfy-duration-slow; easing mirrors --snapfy-ease-standard.
const pageVariants: Variants = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] as const } },
} as const;

const SORT_OPTIONS: SelectOption[] = [
  { value: 'newest', label: 'Mais Recente' },
  { value: 'oldest', label: 'Mais Antigo' },
];

// Label discreto que prefixa um grupo de filtros (TIPO, ORIGEM, CANAL, COLEÇÃO).
const FilterLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="text-[11px] font-semibold uppercase tracking-wider text-snapfy-text-secondary">{children}</span>
);

// Recipe único dos chips de filtro (uma "tier" só): primitivo Button, sem rounded-full.
const FILTER_CHIP = 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-button text-xs !normal-case !tracking-normal !font-semibold';

// Mapeia ids de GalleryItem ("img_123" / "vid_45") para os arrays que a API espera.
const idsToSelection = (ids: string[]): { image_ids: number[]; video_ids: number[] } => {
  const image_ids: number[] = [];
  const video_ids: number[] = [];
  ids.forEach((id) => {
    const n = parseInt(id.replace(/^(img_|vid_)/, ''), 10);
    if (Number.isNaN(n)) return;
    if (id.startsWith('vid_')) video_ids.push(n);
    else image_ids.push(n);
  });
  return { image_ids, video_ids };
};

const GalleryPage: React.FC<GalleryPageProps> = ({ items, onEditItem, onStartVideoCreation }) => {
  const location = useLocation();
  const { user } = useAuthStore();
  const isCompanyUser = !!user?.companyId;
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [selectedCampaignId, setSelectedCampaignId] = useState<number | 'all' | 'playground'>('all');
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);
  // Default 'photos': evita disparar GET /videos no load inicial. Videos so
  // sao buscados quando o usuario abre a aba "Videos".
  const [activeTab, setActiveTab] = useState<'all' | 'photos' | 'videos'>('photos');
  const [mediaFilter, setMediaFilter] = useState<'all' | 'generated' | 'edited' | 'uploaded' | 'grouped'>('all');
  const [channelFilter, setChannelFilter] = useState<ChannelValue>('all');
  const [galleryScope, setGalleryScope] = useState<'mine' | 'team' | 'all'>('mine');
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [displayItems, setDisplayItems] = useState<GalleryItem[]>(items);
  const [groupedItems, setGroupedItems] = useState<GroupedImage[]>([]); // For grouped view
  const [fullscreenImages, setFullscreenImages] = useState<ViewerImage[]>([]);
  const [fullscreenIndex, setFullscreenIndex] = useState(0);
  const [isFullscreenOpen, setIsFullscreenOpen] = useState(false);
  const [tagModalOpen, setTagModalOpen] = useState(false);
  const [currentTagItem, setCurrentTagItem] = useState<GalleryItem | null>(null);
  const [isFilterLoading, setIsFilterLoading] = useState(false);

  // Infinite scroll state
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const isLoadingRef = useRef(false); // Ref to prevent race conditions
  const perPage = 16;

  const prefersReducedMotion = useReducedMotion();

  // Debounced search term to avoid excessive filtering
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  // --- Bulk delete + recycle bin (gallery-bulk-delete) ---
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isMutating, setIsMutating] = useState(false);
  const [isTrashOpen, setIsTrashOpen] = useState(false);
  const [trashItems, setTrashItems] = useState<GalleryItem[]>([]);
  const [trashSelectedIds, setTrashSelectedIds] = useState<Set<string>>(new Set());
  const [isTrashLoading, setIsTrashLoading] = useState(false);

  const toggleSelected = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const exitSelection = useCallback(() => {
    setSelectionMode(false);
    setSelectedIds(new Set());
  }, []);

  const handleBulkDelete = useCallback(async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    // Alerta de cascata: originais com variações/edições agrupadas levam essas junto.
    const groupedVariationCount = groupedItems
      .filter((g) => selectedIds.has(g.id))
      .reduce((sum, g) => sum + (g.variationsCount || 0), 0);
    const cascadeNote = groupedVariationCount > 0
      ? `\n\nAtenção: ${groupedVariationCount} variação(ões)/edição(ões) das imagens originais selecionadas também serão excluídas.`
      : '\n\nObs: imagens originais com edições/variações também removerão essas variações.';
    if (!window.confirm(`Excluir ${ids.length} item(ns)? Vão para a lixeira (recuperáveis por 7 dias).${cascadeNote}`)) return;
    setIsMutating(true);
    try {
      const res = await aiService.bulkDeleteMedia(idsToSelection(ids));
      setDisplayItems((prev) => prev.filter((it) => !selectedIds.has(it.id)));
      setGroupedItems((prev) => prev.filter((g) => !selectedIds.has(g.id)));
      exitSelection();
      const extra = (res.cascadedVariations ?? 0) > 0 ? ` (+${res.cascadedVariations} variações)` : '';
      window.alert(`${res.totalDeleted ?? 0} item(ns) movido(s) para a lixeira${extra}.`);
    } catch (e) {
      console.error('bulk delete failed', e);
      window.alert('Falha ao excluir. Tente novamente.');
    } finally {
      setIsMutating(false);
    }
  }, [selectedIds, groupedItems, exitSelection]);

  const openTrash = useCallback(async () => {
    setIsTrashOpen(true);
    setIsTrashLoading(true);
    setTrashSelectedIds(new Set());
    try {
      const data = await aiService.getTrash({ per_page: 100 });
      const imgs: GalleryItem[] = (data.images || []).map((img: any) => ({
        id: `img_${img.id}`,
        type: 'image' as const,
        src: img.thumbnailUrl || img.url || img.s3Url || '',
        prompt: img.prompt || '',
        createdAt: img.createdAt || img.created_at,
        width: img.width,
        height: img.height,
      }));
      const vids: GalleryItem[] = (data.videos || []).map((vid: any) => ({
        id: `vid_${vid.id}`,
        type: 'video' as const,
        src: vid.url || vid.s3Url || '',
        poster: '',
        prompt: '',
        createdAt: vid.createdAt || vid.created_at,
      }));
      setTrashItems([...imgs, ...vids]);
    } catch (e) {
      console.error('load trash failed', e);
      setTrashItems([]);
    } finally {
      setIsTrashLoading(false);
    }
  }, []);

  const toggleTrashSelected = useCallback((id: string) => {
    setTrashSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleBulkRestore = useCallback(async () => {
    const ids = Array.from(trashSelectedIds);
    if (ids.length === 0) return;
    setIsMutating(true);
    try {
      const res = await aiService.bulkRestoreMedia(idsToSelection(ids));
      setTrashItems((prev) => prev.filter((it) => !trashSelectedIds.has(it.id)));
      setTrashSelectedIds(new Set());
      const extra = (res.cascadedVariations ?? 0) > 0 ? ` (+${res.cascadedVariations} variações)` : '';
      window.alert(`${res.totalRestored ?? 0} item(ns) restaurado(s)${extra}. Atualize a galeria para vê-los.`);
    } catch (e) {
      console.error('bulk restore failed', e);
      window.alert('Falha ao restaurar. Tente novamente.');
    } finally {
      setIsMutating(false);
    }
  }, [trashSelectedIds]);
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  React.useEffect(() => {
    const fetchCampaigns = async () => {
      try {
        const data = await campaignService.getCampaigns();
        setCampaigns(data);
      } catch (error) {
        console.error("Failed to fetch campaigns", error);
      }
    };
    fetchCampaigns();
  }, []);

  // Fetch gallery with pagination
  const fetchGallery = useCallback(async (pageNum: number, append: boolean = false) => {
    // Use ref to prevent race conditions instead of state
    if (isLoadingRef.current) return;

    try {
      isLoadingRef.current = true;
      setIsLoadingMore(true);
      const commonFilters: any = { per_page: perPage, page: pageNum, scope: galleryScope };
      if (selectedCampaignId !== 'all') {
        commonFilters.campaign_id = selectedCampaignId === 'playground' ? 'null' : selectedCampaignId;
      }

      const shouldFetchImages = activeTab === 'all' || activeTab === 'photos';
      const shouldFetchVideos = activeTab === 'all' || activeTab === 'videos';

      const imageFilters = { ...commonFilters } as any;
      if (selectedTagIds.length > 0) {
        imageFilters.tag_ids = selectedTagIds;
      }

      // Check if we're in grouped mode
      const isGroupedMode = (activeTab === 'photos' || activeTab === 'all') && mediaFilter === 'grouped';

      // Apply sub-filter if we are in 'all' or 'photos' tab (not videos-only)
      if ((activeTab === 'all' || activeTab === 'photos') && mediaFilter !== 'all' && mediaFilter !== 'grouped') {
        imageFilters.status = mediaFilter;
      }

      // Add grouped parameter for grouped view
      if (isGroupedMode) {
        imageFilters.grouped = true;
      }

      // Channel filter
      if (channelFilter !== 'all') {
        imageFilters.channel = channelFilter;
      }

      // Inherit shared filters (scope + campaign_id, including Playground 'null')
      // so videos respect enterprise scope and collection like images do.
      const videoFilters: any = { ...commonFilters };
      // Add video status filter for 'all' tab
      if (activeTab === 'all' && mediaFilter !== 'all' && mediaFilter !== 'grouped') {
        videoFilters.status = mediaFilter;
      }

      const [images, videos] = await Promise.all([
        shouldFetchImages ? aiService.getUserImages(imageFilters) : Promise.resolve([]),
        shouldFetchVideos && !isGroupedMode ? aiService.getUserVideos(videoFilters) : Promise.resolve([])
      ]);

      // Handle grouped mode differently
      if (isGroupedMode) {
        const grouped: GroupedImage[] = (images as any[]).map((img: any) => ({
          id: `img_${img.id}`,
          src: img.url || img.s3Url || '',
          thumbnailUrl: img.thumbnailUrl || img.url || img.s3Url || '',
          width: img.width,
          height: img.height,
          variationsCount: img.variationsCount || 0,
          variations: (img.variations || []).map((v: any) => ({
            id: v.id,
            url: v.url || v.s3Url || '',
            thumbnailUrl: v.thumbnailUrl || v.url || v.s3Url || '',
            width: v.width,
            height: v.height,
            createdAt: v.createdAt || v.created_at,
          })),
          tags: img.tags || [],
          createdAt: img.createdAt || img.created_at,
        }));

        const hasMoreItems = grouped.length >= perPage;
        setHasMore(hasMoreItems);

        if (append) {
          setGroupedItems(prev => [...prev, ...grouped]);
        } else {
          setGroupedItems(grouped);
        }
        setDisplayItems([]); // Clear regular items in grouped mode
      } else {
        // Regular mode
        const imageItems: GalleryItem[] = (images as any[]).map((img: any) => ({
          id: `img_${img.id}`,
          type: 'image',
          src: img.url || img.s3Url || '',
          thumbnailUrl: img.thumbnailUrl || img.url || img.s3Url || '',
          prompt: img.prompt || '',
          createdAt: img.createdAt || img.created_at,
          campaignId: img.campaignId || img.campaign_id ? Number(img.campaignId || img.campaign_id) : undefined,
          channel: img.channel || null,
          tags: img.tags || [],
          width: img.width,
          height: img.height,
          author: img.author || null,
        }));

        const videoItems: GalleryItem[] = (videos as any[]).map((vid: any) => ({
          id: `vid_${vid.id}`,
          type: 'video',
          src: vid.url || vid.s3Url || '',
          poster: '',
          prompt: '',
          createdAt: vid.createdAt,
          campaignId: vid.campaignId ? Number(vid.campaignId) : undefined,
          author: vid.author || null,
        }));

        const merged = [...imageItems, ...videoItems];

        // Check if we have more items to load
        const hasMoreItems = merged.length >= perPage;
        setHasMore(hasMoreItems);

        if (append) {
          setDisplayItems(prev => [...prev, ...merged]);
        } else {
          setDisplayItems(merged);
        }
        setGroupedItems([]); // Clear grouped items in regular mode
      }
    } catch (error) {
      console.error("Failed to fetch gallery", error);
    } finally {
      isLoadingRef.current = false;
      setIsLoadingMore(false);
    }
  }, [selectedCampaignId, selectedTagIds, activeTab, mediaFilter, channelFilter, galleryScope]);

  // Initial load and filter changes
  React.useEffect(() => {
    setPage(1);
    setHasMore(true);
    setIsFilterLoading(true);
    fetchGallery(1, false).finally(() => setIsFilterLoading(false));
  }, [selectedCampaignId, selectedTagIds, activeTab, mediaFilter, channelFilter, galleryScope]);

  // Load more handler for infinite scroll
  const loadMore = useCallback(() => {
    if (!isLoadingMore && hasMore) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchGallery(nextPage, true);
    }
  }, [page, isLoadingMore, hasMore, fetchGallery]);


  const filteredAndSortedItems = useMemo(() => {
    let result = [...displayItems];

    // Filter by tab (all/photos/videos)
    if (activeTab === 'photos') {
      result = result.filter(item => item.type === 'image');
    } else if (activeTab === 'videos') {
      result = result.filter(item => item.type === 'video');
    }

    // Search by date or prompt - supports multiple formats:
    // dd/mm/yyyy, yyyy-mm-dd, dd/mm, mm/yyyy, yyyy, or partial matches
    // Also searches by prompt content
    if (debouncedSearchTerm) {
      const term = debouncedSearchTerm.toLowerCase().trim();
      result = result.filter(item => {
        // Search in prompt
        if (item.prompt && item.prompt.toLowerCase().includes(term)) {
          return true;
        }

        // Search by date
        const date = new Date(item.createdAt);
        // Format date in multiple ways for flexible matching
        const ptBrDate = date.toLocaleDateString('pt-BR'); // dd/mm/yyyy
        const isoDate = (item.createdAt || '').split('T')[0]; // yyyy-mm-dd
        const year = date.getFullYear().toString();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const monthYear = `${month}/${year}`; // mm/yyyy
        const dayMonth = `${day}/${month}`; // dd/mm

        return (
          ptBrDate.includes(term) ||
          isoDate.includes(term) ||
          year.includes(term) ||
          monthYear.includes(term) ||
          dayMonth.includes(term)
        );
      });
    }

    switch (sortBy) {
      case 'newest':
        result.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        break;
      case 'oldest':
        result.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
        break;
    }

    return result;
  }, [displayItems, debouncedSearchTerm, sortBy, activeTab]);

  // Filtered and sorted grouped items for grouped view
  const filteredAndSortedGroupedItems = useMemo(() => {
    let result = [...groupedItems];

    // Search by date or tag name (grouped items have no prompt field).
    // Mirrors the flat gallery's date formats so the search box isn't a no-op here.
    if (debouncedSearchTerm) {
      const term = debouncedSearchTerm.toLowerCase().trim();
      result = result.filter(item => {
        if (item.tags?.some(tag => tag.name.toLowerCase().includes(term))) {
          return true;
        }
        const date = new Date(item.createdAt);
        const ptBrDate = date.toLocaleDateString('pt-BR'); // dd/mm/yyyy
        const isoDate = (item.createdAt || '').split('T')[0]; // yyyy-mm-dd
        const year = date.getFullYear().toString();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const monthYear = `${month}/${year}`; // mm/yyyy
        const dayMonth = `${day}/${month}`; // dd/mm
        return (
          ptBrDate.includes(term) ||
          isoDate.includes(term) ||
          year.includes(term) ||
          monthYear.includes(term) ||
          dayMonth.includes(term)
        );
      });
    }

    // Sort
    switch (sortBy) {
      case 'newest':
        result.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        break;
      case 'oldest':
        result.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
        break;
    }

    return result;
  }, [groupedItems, sortBy, debouncedSearchTerm]);

  // Check if we're in grouped mode
  const isGroupedMode = (activeTab === 'photos' || activeTab === 'all') && mediaFilter === 'grouped';

  // Build campaign options for Select primitive
  const campaignOptions: SelectOption[] = useMemo(() => [
    { value: 'all', label: 'Todas as Coleções' },
    { value: 'playground', label: 'Playground (Sem Campanha)' },
    ...campaigns.map(c => ({ value: String(c.id), label: c.name })),
  ], [campaigns]);

  const handleDownload = (id: string, filename: string) => {
    // Extract numeric ID from "img_123" or "vid_123"
    const numericId = id.replace(/^(img_|vid_)/, '');
    const isVideo = id.startsWith('vid_');

    // Construct proxy URL
    // Use the configured API URL from environment or default
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';
    const proxyUrl = `${apiUrl}/download/${isVideo ? 'videos' : 'images'}/${numericId}/proxy`;

    const token = localStorage.getItem('auth_token'); // Correct key is 'auth_token'

    fetch(proxyUrl, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/json' // or image/*
      }
    })
      .then(response => {
        if (!response.ok) throw new Error('Download failed');
        return response.blob();
      })
      .then(blob => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      })
      .catch(err => {
        console.error("Download error:", err);
        // Fallback or error notification
        alert("Erro ao baixar arquivo. Tente novamente.");
      });
  };

  const handleAddTag = async (itemId: string, tagIds: number[]) => {
    const numericId = parseInt(itemId.replace(/^(img_|vid_)/, ''));
    const isVideo = itemId.startsWith('vid_');

    // Currently only supporting images for tags in UI for now, or update backend for videos too
    // Backend supports both, but let's assume images for now as per request
    if (isVideo) return; // TODO: Implement for videos

    try {
      await tagService.syncTagsForImage(numericId, tagIds);
      // Update local state
      setDisplayItems(prev => prev.map(item => {
        if (item.id === itemId) {
          // We need the full tag objects, but we only have IDs here.
          // Ideally we should refetch or have the tag objects.
          // For now, let's just trigger a refetch or optimistic update if we had the tag objects.
          // A simple way is to refetch the gallery or just the item.
          // Let's refetch gallery for simplicity or just ignore visual update until refresh
        }
        return item;
      }));
      // Trigger refetch to get updated tags with colors/names
      const filters: any = { per_page: 100 };
      if (selectedCampaignId !== 'all') filters.campaign_id = selectedCampaignId === 'playground' ? 'null' : selectedCampaignId;
      if (selectedTagIds.length > 0) filters.tag_ids = selectedTagIds;

      const images = await aiService.getUserImages(filters);
      const galleryItems: GalleryItem[] = images.map((img: any) => ({
        id: `img_${img.id}`,
        type: 'image',
        src: img.url || img.s3Url || '',
        prompt: '',
        createdAt: img.createdAt,
        campaignId: img.campaignId ? Number(img.campaignId) : undefined,
        tags: img.tags || []
      }));
      setDisplayItems(galleryItems);

    } catch (error) {
      console.error("Failed to update tags", error);
    }
  };

  return (
    <motion.div
      className="w-full space-y-8"
      variants={prefersReducedMotion ? undefined : pageVariants}
      initial={prefersReducedMotion ? undefined : 'hidden'}
      animate={prefersReducedMotion ? undefined : 'visible'}
    >
      <PageHeader
        title={galleryScope === 'mine' ? 'Sua Galeria' : galleryScope === 'team' ? 'Galeria da Equipe' : 'Galeria da Empresa'}
        subtitle={galleryScope === 'mine' ? 'Todas as imagens e vídeos que você gerou na plataforma.' : 'Imagens e vídeos gerados por membros da sua empresa.'}
        infoTooltip="Acesse e gerencie todas as imagens e vídeos que você gerou."
        icon={
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} className="w-full h-full">
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        }
      />

      {/* Toolbar: linha primária (busca + ordenação) + linha secundária (filtros) */}
      <div className="bg-snapfy-surface p-4 rounded-xl flex flex-col gap-3 sticky top-6 z-10 border border-white/10">

        {/* Linha primária: campo de busca à esquerda, ordenação à direita */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full">
          <div className="relative flex-grow w-full">
            <Input
              type="search"
              placeholder="Buscar por prompt, data ou campanha..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none" style={{ top: 0 }}>
              <SearchIcon className="text-snapfy-text-secondary" />
            </div>
          </div>

          <div className="w-full sm:w-52 shrink-0">
            <Select
              options={SORT_OPTIONS}
              value={sortBy}
              onChange={(val) => setSortBy(val)}
            />
          </div>
        </div>

        {/* Linha 2 — TIPO + ORIGEM (+ agrupadas) + VISÃO (enterprise) */}
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 w-full">
          {/* TIPO */}
          <div className="flex items-center gap-2">
            <FilterLabel>Tipo</FilterLabel>
            <Button variant={activeTab === 'all' ? 'solid' : 'ghost'} className={FILTER_CHIP} onClick={() => setActiveTab('all')}>Tudo</Button>
            <Button variant={activeTab === 'photos' ? 'solid' : 'ghost'} leadingIcon={<ImageIcon size={14} />} className={FILTER_CHIP} onClick={() => setActiveTab('photos')}>Fotos</Button>
            <Button variant={activeTab === 'videos' ? 'solid' : 'ghost'} leadingIcon={<Video size={14} />} className={FILTER_CHIP} onClick={() => setActiveTab('videos')}>Vídeos</Button>
          </div>

          {/* ORIGEM (Tudo e Fotos) */}
          {(activeTab === 'all' || activeTab === 'photos') && (
            <>
              <div className="flex items-center gap-2">
                <FilterLabel>Origem</FilterLabel>
                <Button variant={mediaFilter === 'all' ? 'solid' : 'ghost'} className={FILTER_CHIP} onClick={() => setMediaFilter('all')}>Todas</Button>
                <Button variant={mediaFilter === 'generated' ? 'solid' : 'ghost'} leadingIcon={<Sparkles size={14} />} className={FILTER_CHIP} onClick={() => setMediaFilter('generated')}>Geradas</Button>
                <Button variant={mediaFilter === 'edited' ? 'solid' : 'ghost'} leadingIcon={<Pencil size={14} />} className={FILTER_CHIP} onClick={() => setMediaFilter('edited')}>Editadas</Button>
                <Button variant={mediaFilter === 'uploaded' ? 'solid' : 'ghost'} leadingIcon={<Upload size={14} />} className={FILTER_CHIP} onClick={() => setMediaFilter('uploaded')}>Enviadas</Button>
              </div>
              <Button variant={mediaFilter === 'grouped' ? 'solid' : 'ghost'} leadingIcon={<Layers size={14} />} className={FILTER_CHIP} onClick={() => setMediaFilter('grouped')} title="Agrupar originais com suas versões editadas">Fotos agrupadas</Button>
            </>
          )}

          {/* VISÃO (enterprise) */}
          {isCompanyUser && (
            <div className="flex items-center gap-2">
              <FilterLabel>Visão</FilterLabel>
              <Button variant={galleryScope === 'mine' ? 'solid' : 'ghost'} className={FILTER_CHIP} onClick={() => setGalleryScope('mine')}>Minhas</Button>
              <Button variant={galleryScope === 'team' ? 'solid' : 'ghost'} className={FILTER_CHIP} onClick={() => setGalleryScope('team')}>Da equipe</Button>
              <Button variant={galleryScope === 'all' ? 'solid' : 'ghost'} className={FILTER_CHIP} onClick={() => setGalleryScope('all')}>Todas</Button>
            </div>
          )}
        </div>

        {/* Linha 3 — CANAL + COLEÇÃO + tags + ações (Selecionar / Excluídas) */}
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 w-full">
          {/* CANAL — não se aplica a vídeos */}
          {activeTab !== 'videos' && (
            <div className="flex items-center gap-2">
              <FilterLabel>Canal</FilterLabel>
              <ChannelFilterDropdown value={channelFilter} onChange={setChannelFilter} hideLabel />
            </div>
          )}

          {/* COLEÇÃO */}
          <div className="flex items-center gap-2">
            <FilterLabel>Coleção</FilterLabel>
            <div className="w-auto min-w-[170px]">
              <Select
                options={campaignOptions}
                className="!px-3 !py-1.5 !text-xs !normal-case !tracking-normal !font-semibold !text-snapfy-text-primary"
                value={selectedCampaignId === 'all' ? 'all' : selectedCampaignId === 'playground' ? 'playground' : String(selectedCampaignId)}
                onChange={(val) => {
                  if (val === 'all') setSelectedCampaignId('all');
                  else if (val === 'playground') setSelectedCampaignId('playground');
                  else setSelectedCampaignId(Number(val));
                }}
              />
            </div>
          </div>

          {/* Tags — não se aplica a vídeos */}
          {activeTab !== 'videos' && (
            <div className="w-full sm:w-56">
              <TagSelector
                selectedTags={selectedTagIds}
                onChange={setSelectedTagIds}
                placeholder="Filtrar por tags..."
              />
            </div>
          )}

          {/* Ações à direita: Selecionar + Excluídas */}
          <div className="flex items-center gap-2 ml-auto">
            <Button
              variant={selectionMode ? 'solid' : 'ghost'}
              leadingIcon={<CheckCircle2 size={14} />}
              className={FILTER_CHIP}
              onClick={() => (selectionMode ? exitSelection() : setSelectionMode(true))}
            >
              {selectionMode ? 'Cancelar seleção' : 'Selecionar'}
            </Button>
            <Button
              variant="ghost"
              leadingIcon={<Trash2 size={14} />}
              className={`${FILTER_CHIP} !text-snapfy-error hover:!text-snapfy-error hover:!border-snapfy-error`}
              onClick={openTrash}
              title="Mídias excluídas (lixeira)"
            >
              Excluídas
            </Button>
          </div>
        </div>
      </div>

      {/* Loading State for Filter Changes */}
      {isFilterLoading ? (
        <div className="flex items-center justify-center" style={{ height: 'calc(100vh - 300px)', minHeight: '500px' }}>
          <div className="flex flex-col items-center gap-3">
            <svg className="animate-spin h-12 w-12 text-snapfy-primary" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span className="text-snapfy-text-secondary text-sm">Carregando...</span>
          </div>
        </div>
      ) : isGroupedMode ? (
        /* Grouped View - Using standard grid for expandable cards */
        <div
          className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 pb-8"
          style={{ minHeight: '500px' }}
        >
          {filteredAndSortedGroupedItems.length === 0 ? (
            <div className="col-span-full flex flex-col items-center justify-center py-20 text-center space-y-4">
              <div className="text-6xl">📚</div>
              <h3 className="text-xl font-semibold text-snapfy-text-primary">Nenhuma imagem agrupada</h3>
              <p className="text-snapfy-text-secondary max-w-md">
                Edite algumas imagens primeiro. Quando você editar uma imagem, a versão original aparecerá aqui com todas as suas versões editadas agrupadas.
              </p>
            </div>
          ) : (
            filteredAndSortedGroupedItems.map((groupedItem) => (
              <GroupedImageCard
                key={groupedItem.id}
                id={groupedItem.id}
                src={groupedItem.src}
                thumbnailUrl={groupedItem.thumbnailUrl}
                width={groupedItem.width}
                height={groupedItem.height}
                variations={groupedItem.variations}
                variationsCount={groupedItem.variationsCount}
                tags={groupedItem.tags}
                onEditItem={onEditItem}
                onStartVideoCreation={onStartVideoCreation}
                onDownload={handleDownload}
                onImageClick={(imageId) => {
                  // Build viewer images from parent + variations
                  const allImages: ViewerImage[] = [
                    { id: groupedItem.id, src: groupedItem.src, prompt: '', width: groupedItem.width, height: groupedItem.height },
                    ...groupedItem.variations.map(v => ({ id: `img_${v.id}`, src: v.url, prompt: '', width: v.width, height: v.height }))
                  ];
                  const imageIndex = allImages.findIndex(i => i.id === imageId);
                  setFullscreenImages(allImages);
                  setFullscreenIndex(imageIndex >= 0 ? imageIndex : 0);
                  setIsFullscreenOpen(true);
                }}
                onTagManage={(item) => {
                  setCurrentTagItem(item);
                  setTagModalOpen(true);
                }}
              />
            ))
          )}
        </div>
      ) : (
        /* Regular View - Using VirtuosoGrid for performance */
        <div id="galleryGrid">
          <VirtuosoGrid
            useWindowScroll
            style={{ minHeight: '500px' }}
            totalCount={filteredAndSortedItems.length}
            overscan={200}
            endReached={loadMore}
            listClassName="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4"
            itemClassName="w-full"
            itemContent={(index) => {
              const item = filteredAndSortedItems[index];
              if (!item) return null;

              return (
                <div className={`group relative rounded-card overflow-hidden border shadow-card ${selectionMode && selectedIds.has(item.id) ? 'border-snapfy-primary ring-2 ring-snapfy-primary' : 'border-white/10'}`}>
                  {/* Checkbox de seleção: só aparece no modo seleção (ativado pelo botão "Selecionar"). */}
                  {selectionMode && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); toggleSelected(item.id); }}
                      className={`absolute top-2 left-2 z-40 w-7 h-7 rounded-md border-2 flex items-center justify-center text-sm font-bold ${selectedIds.has(item.id) ? 'bg-snapfy-primary border-snapfy-primary text-white' : 'bg-black/50 border-white/80 text-transparent'}`}
                      title="Selecionar"
                      aria-pressed={selectedIds.has(item.id)}
                    >
                      ✓
                    </button>
                  )}
                  {item.type === 'image' ? (
                    <img
                      src={(item as any).thumbnailUrl || item.src}
                      alt="Imagem gerada"
                      loading="lazy"
                      decoding="async"
                      className="w-full aspect-square object-cover rounded-card cursor-pointer bg-snapfy-inner transition-transform duration-[700ms] group-hover:scale-110"
                      onClick={() => {
                        const imageItems = filteredAndSortedItems.filter(i => i.type === 'image');
                        const viewerImages: ViewerImage[] = imageItems.map(i => ({
                          id: i.id,
                          src: i.src,
                          prompt: '',
                          width: i.width,
                          height: i.height
                        }));
                        const imageIndex = imageItems.findIndex(i => i.id === item.id);
                        setFullscreenImages(viewerImages);
                        setFullscreenIndex(imageIndex);
                        setIsFullscreenOpen(true);
                      }}
                    />
                  ) : (
                    <video
                      src={item.src}
                      poster={item.poster}
                      controls
                      preload="none"
                      className="w-full aspect-square object-cover rounded-card bg-snapfy-inner"
                    />
                  )}

                  {/* Channel Badge - top-left */}
                  {item.channel === 'whatsapp' && (
                    <div className="absolute top-2 left-2 z-20">
                      <span className="flex items-center gap-1 text-[10px] font-medium text-green-300 bg-green-900/70 backdrop-blur-sm px-1.5 py-0.5 rounded-full">
                        {/* WhatsApp brand green #25D366 — sanctioned residual (Phase 7) */}
                        <svg className="w-2.5 h-2.5" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                        WA
                      </span>
                    </div>
                  )}

                  {/* Author Badge - top-left (when viewing company gallery) */}
                  {item.author && galleryScope !== 'mine' && (
                    <div className={`absolute top-2 ${item.channel === 'whatsapp' ? 'left-14' : 'left-2'} z-20`}>
                      <span className="flex items-center gap-1 text-[10px] font-medium text-cyan-300 bg-cyan-900/70 backdrop-blur-sm px-1.5 py-0.5 rounded-full truncate max-w-[120px]">
                        <svg className="w-2.5 h-2.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        {item.author.name.split(' ')[0]}
                      </span>
                    </div>
                  )}

                  {/* Dimensions Badge - discrete, bottom-right */}
                  {item.type === 'image' && item.width && item.height && (
                    <div className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                      <span className="text-[10px] text-white/80 bg-black/50 backdrop-blur-sm px-1.5 py-0.5 rounded">
                        {item.width}×{item.height}
                      </span>
                    </div>
                  )}

                  {/* Tags Overlay */}
                  {item.tags && item.tags.length > 0 && (
                    <div className="absolute bottom-2 left-2 flex flex-wrap gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-20">
                      {item.tags.map(tag => (
                        <span
                          key={tag.id}
                          className="px-1.5 py-0.5 rounded text-[10px] font-medium text-white shadow-sm"
                          style={{ backgroundColor: tag.color }}
                        >
                          {tag.name}
                        </span>
                      ))}
                    </div>
                  )}

                  {item.type === 'video' && (
                    <div className="absolute top-2 right-2 z-30 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-auto">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownload(item.id, `snapfy-gallery-${item.id}.mp4`);
                        }}
                        className="p-2 bg-black/60 rounded-full text-white hover:bg-snapfy-primary focus:opacity-100 focus-visible:ring-snapfy-focus"
                        title="Baixar Vídeo"
                      >
                        <DownloadIcon className="w-5 h-5" />
                      </button>
                    </div>
                  )}

                  {item.type === 'image' && (
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/60 transition-all duration-300 rounded-card flex flex-col items-center justify-center p-4 text-center pointer-events-none">
                      <div className="absolute top-2 right-2 flex flex-col gap-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-auto">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownload(item.id, `snapfy-gallery-${item.id}.${getImageExtFromUrl(item.src)}`);
                          }}
                          className="p-2 bg-black/50 rounded-full text-white hover:bg-snapfy-primary focus:opacity-100 focus-visible:ring-snapfy-focus"
                          title="Baixar Imagem"
                        >
                          <DownloadIcon className="w-5 h-5" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onEditItem(item);
                          }}
                          className="p-2 bg-black/50 rounded-full text-white hover:bg-snapfy-primary focus:opacity-100 focus-visible:ring-snapfy-focus"
                          title="Edição Rápida"
                        >
                          <EditIcon className="w-5 h-5" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onStartVideoCreation(item);
                          }}
                          className="p-2 bg-black/50 rounded-full text-white hover:bg-snapfy-primary focus:opacity-100 focus-visible:ring-snapfy-focus"
                          title="Criar Vídeo"
                        >
                          <VideoIcon className="w-5 h-5" />
                        </button>

                        {/* Tag Edit Button */}
                        <button
                          className="p-2 bg-black/50 rounded-full text-white hover:bg-snapfy-primary focus:opacity-100 focus-visible:ring-snapfy-focus"
                          title="Gerenciar Tags"
                          onClick={(e) => {
                            e.stopPropagation();
                            setCurrentTagItem(item);
                            setTagModalOpen(true);
                          }}
                        >
                          <TagIcon className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            }}
            components={{
              Footer: () => (
                isLoadingMore ? (
                  <div className="flex justify-center py-8">
                    <svg className="animate-spin h-8 w-8 text-snapfy-primary" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </div>
                ) : hasMore ? (
                  <div className="text-center py-4 text-snapfy-text-secondary text-sm">
                    Role para carregar mais...
                  </div>
                ) : filteredAndSortedItems.length > 0 ? (
                  <div className="text-center py-4 text-snapfy-text-secondary text-sm">
                    Fim da galeria • {filteredAndSortedItems.length} itens
                  </div>
                ) : null
              )
            }}
          />
        </div>
      )}
      {filteredAndSortedItems.length === 0 && !isFilterLoading && (
        <div className="flex flex-col items-center justify-center py-20 text-center space-y-6">
          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-r from-snapfy-primary/20 to-snapfy-accent-soft/20 rounded-full blur-xl"></div>
            <svg xmlns="http://www.w3.org/2000/svg" className="h-20 w-20 text-snapfy-text-secondary relative z-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 7h4l2-2h6l2 2h4v12a2 2 0 01-2 2H5a2 2 0 01-2-2V7zm3 4a2 2 0 104 0 2 2 0 00-4 0z" />
            </svg>
          </div>

          <div className="max-w-md space-y-2">
            <h3 className="text-2xl font-bold text-snapfy-text-primary">{activeTab === 'videos' ? 'Nenhum vídeo ainda' : activeTab === 'photos' ? 'Nenhuma foto ainda' : 'Nenhuma imagem ou vídeo ainda'}</h3>
            <p className="text-snapfy-text-secondary">
              {activeTab === 'videos'
                ? 'Gere uma imagem na página Início ou no módulo Fashion. Depois, use "Criar Vídeo" na galeria.'
                : 'Você pode gerar imagens na página Início ou criar em lote no módulo Fashion.'}
            </p>
          </div>

          <div className="flex gap-4 text-sm text-snapfy-text-secondary">
            <Link
              to={APP_HOME}
              className="flex items-center gap-2 px-4 py-2 bg-snapfy-surface-hover/50 hover:bg-snapfy-inner/50 hover:text-snapfy-text-primary rounded-xl border border-snapfy-border transition-all cursor-pointer"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" /></svg>
              <span>{activeTab === 'videos' ? 'Gerar Imagem' : 'Início'}</span>
            </Link>
            <Link
              to={studioPath('/batch', location.pathname)}
              className="flex items-center gap-2 px-4 py-2 bg-snapfy-surface-hover/50 hover:bg-snapfy-inner/50 hover:text-snapfy-text-primary rounded-xl border border-snapfy-border transition-all cursor-pointer"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" /></svg>
              <span>Fashion</span>
            </Link>
          </div>
        </div>
      )
      }

      {/* Barra de ação em massa (exclusão) */}
      {selectionMode && selectedIds.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-4 bg-snapfy-surface border border-white/15 shadow-card rounded-full px-5 py-3">
          <span className="text-sm text-snapfy-text-primary">{selectedIds.size} selecionado(s)</span>
          <Button variant="ghost" className="px-3 py-1 rounded-full text-sm" onClick={exitSelection} disabled={isMutating}>
            Cancelar
          </Button>
          <Button
            variant="danger"
            leadingIcon={<Trash2 size={15} />}
            className="px-4 py-1 rounded-full text-sm"
            onClick={handleBulkDelete}
            disabled={isMutating}
          >
            {isMutating ? 'Excluindo...' : `Excluir ${selectedIds.size}`}
          </Button>
        </div>
      )}

      {/* Lixeira (mídias excluídas) */}
      {isTrashOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setIsTrashOpen(false)}>
          <div className="bg-snapfy-surface rounded-xl border border-white/10 w-full max-w-5xl max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <div>
                <h3 className="text-lg font-bold text-snapfy-text-primary">Mídias excluídas</h3>
                <p className="text-xs text-snapfy-text-secondary">Recuperáveis por 7 dias; depois somem da lixeira (mantidas para auditoria).</p>
              </div>
              <button className="text-snapfy-text-secondary hover:text-white text-2xl leading-none" onClick={() => setIsTrashOpen(false)} title="Fechar">×</button>
            </div>
            <div className="p-4 overflow-y-auto flex-1">
              {isTrashLoading ? (
                <div className="text-center py-12 text-snapfy-text-secondary">Carregando...</div>
              ) : trashItems.length === 0 ? (
                <div className="text-center py-12 text-snapfy-text-secondary">A lixeira está vazia.</div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                  {trashItems.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => toggleTrashSelected(item.id)}
                      className={`relative rounded-card overflow-hidden border-2 ${trashSelectedIds.has(item.id) ? 'border-snapfy-primary' : 'border-white/10'}`}
                    >
                      {item.type === 'image' ? (
                        <img src={item.src} alt="" loading="lazy" className="w-full aspect-square object-cover bg-snapfy-inner" />
                      ) : (
                        <video src={item.src} className="w-full aspect-square object-cover bg-snapfy-inner" />
                      )}
                      {trashSelectedIds.has(item.id) && (
                        <span className="absolute top-1 right-1 w-5 h-5 rounded-md bg-snapfy-primary text-white text-xs flex items-center justify-center">✓</span>
                      )}
                      {item.type === 'video' && (
                        <span className="absolute bottom-1 left-1 text-[10px] bg-black/60 text-white px-1 rounded">vídeo</span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="flex items-center justify-between p-4 border-t border-white/10">
              <span className="text-sm text-snapfy-text-secondary">{trashSelectedIds.size} selecionado(s)</span>
              <Button
                variant="solid"
                className="px-4 py-1 rounded-full text-sm"
                onClick={handleBulkRestore}
                disabled={isMutating || trashSelectedIds.size === 0}
              >
                {isMutating ? 'Restaurando...' : 'Restaurar selecionadas'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Fullscreen Image Viewer */}
      <FullscreenImageViewer
        isOpen={isFullscreenOpen}
        images={fullscreenImages}
        currentIndex={fullscreenIndex}
        onClose={() => setIsFullscreenOpen(false)}
        onNavigate={setFullscreenIndex}
        onDownload={(imageId) => {
          // O viewer exibe apenas imagens; pega o src da propria lista do viewer
          // (cobre variacoes que nao estao em displayItems). Antes usava
          // 'galleryItems', que so existe no escopo de handleAddTag -> ReferenceError.
          const img = fullscreenImages.find(i => i.id === imageId);
          handleDownload(imageId, `snapfy-gallery-${imageId}.${getImageExtFromUrl(img?.src)}`);
        }}
      />

      {/* Tag Management Modal */}
      {
        currentTagItem && (
          <TagManagementModal
            isOpen={tagModalOpen}
            imageId={currentTagItem.id}
            currentTags={currentTagItem.tags || []}
            onClose={() => {
              setTagModalOpen(false);
              setCurrentTagItem(null);
            }}
            onTagsUpdated={(tags) => {
              // Update the item's tags in the local state
              setDisplayItems(prev => prev.map(item =>
                item.id === currentTagItem.id ? { ...item, tags } : item
              ));
            }}
          />
        )
      }
    </motion.div>
  );
};

export default GalleryPage;
